import React, { useState, useEffect } from 'react';
import { X, Send, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { t } from '../utils/translations';
import { localeFor } from '../utils/intlLocale';

export interface FloatingNotificationData {
  id: string;
  candidateId: string;
  candidateName: string;
  candidatePhone?: string;
  candidateAvatar?: string;
  message: string;
  timestamp: Date;
  type: 'emily_message' | 'candidate_recommendation';
}

interface FloatingNotificationProps {
  notification: FloatingNotificationData;
  language: string;
  onClose: () => void;
  onReply?: (message: string) => Promise<void>;
  onOpenConversation?: () => void;
}

const FloatingNotification: React.FC<FloatingNotificationProps> = ({
  notification,
  language,
  onClose,
  onReply,
  onOpenConversation,
}) => {
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();

  const handleReply = async () => {
    if (!replyText.trim() || !onReply) return;
    setSending(true);
    try {
      await onReply(replyText);
      setReplyText('');
      setExpanded(false);
    } catch (err) {
      console.error('Reply failed:', err);
    } finally {
      setSending(false);
    }
  };

  const handleOpenConversation = () => {
    if (onOpenConversation) {
      onOpenConversation();
    } else {
      navigate(`/conversations?cid=${notification.candidateId}&tab=whatsapp`);
    }
    onClose();
  };

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 20,
        right: 20,
        width: expanded ? 380 : 320,
        background: 'var(--white)',
        borderRadius: 12,
        boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
        border: '1px solid var(--gray-200)',
        overflow: 'hidden',
        zIndex: 9998,
        animation: 'slideIn 0.3s ease-out',
      }}
    >
      <style>{`
        @keyframes slideIn {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>

      {/* Header */}
      <div
        style={{
          padding: '12px 16px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
          {notification.candidateAvatar ? (
            <img
              src={notification.candidateAvatar}
              alt={notification.candidateName}
              style={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                objectFit: 'cover',
                flexShrink: 0,
              }}
            />
          ) : (
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 16,
                flexShrink: 0,
              }}
            >
              {notification.candidateName.charAt(0).toUpperCase()}
            </div>
          )}
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {notification.candidateName}
            </div>
            <div style={{ fontSize: 11, opacity: 0.9 }}>
              {notification.timestamp.toLocaleTimeString(localeFor(language), { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'rgba(255,255,255,0.2)',
            border: 'none',
            color: 'white',
            cursor: 'pointer',
            borderRadius: 4,
            padding: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <X size={16} />
        </button>
      </div>

      {/* Message */}
      <div
        style={{
          padding: '12px 16px',
          borderBottom: '1px solid var(--gray-100)',
          maxHeight: expanded ? 'auto' : 60,
          overflow: 'hidden',
          cursor: 'pointer',
        }}
        onClick={() => !expanded && setExpanded(true)}
      >
        <p
          style={{
            margin: 0,
            fontSize: 13,
            color: 'var(--gray-700)',
            lineHeight: 1.5,
            wordBreak: 'break-word',
          }}
        >
          {notification.message}
        </p>
      </div>

      {/* Reply Input (expanded) */}
      {expanded && (
        <div style={{ padding: '12px 16px', borderTop: '1px solid var(--gray-100)' }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <input
              type="text"
              placeholder={`${t(language, 'notifReply')}…`}
              value={replyText}
              onChange={e => setReplyText(e.target.value)}
              onKeyPress={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleReply();
                }
              }}
              style={{
                flex: 1,
                padding: '8px 12px',
                border: '1px solid var(--gray-200)',
                borderRadius: 6,
                fontSize: 13,
                fontFamily: 'inherit',
                outline: 'none',
              }}
              autoFocus
            />
            <button
              onClick={handleReply}
              disabled={!replyText.trim() || sending}
              style={{
                padding: '8px 12px',
                background: replyText.trim() && !sending ? 'var(--pink)' : 'var(--gray-200)',
                color: replyText.trim() && !sending ? 'white' : 'var(--gray-400)',
                border: 'none',
                borderRadius: 6,
                cursor: replyText.trim() && !sending ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              {sending ? '...' : <Send size={14} />}
            </button>
          </div>

          {/* Action buttons */}
          <button
            onClick={handleOpenConversation}
            style={{
              width: '100%',
              padding: '8px 12px',
              background: 'var(--blue-50)',
              border: '1px solid var(--blue-200)',
              color: 'var(--blue-600)',
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }}
          >
            <ExternalLink size={12} />
            {t(language, 'dbOpenConversation')}
          </button>
        </div>
      )}

      {/* Collapsed state action */}
      {!expanded && (
        <div
          style={{
            padding: '8px 16px',
            display: 'flex',
            gap: 8,
            background: 'var(--gray-50)',
          }}
        >
          <button
            onClick={() => setExpanded(true)}
            style={{
              flex: 1,
              padding: '6px 12px',
              background: 'var(--white)',
              border: '1px solid var(--gray-200)',
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: 600,
              color: 'var(--gray-700)',
            }}
          >
            {t(language, 'notifReply')}
          </button>
          <button
            onClick={handleOpenConversation}
            style={{
              flex: 1,
              padding: '6px 12px',
              background: 'var(--pink)',
              color: 'white',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            {t(language, 'open')}
          </button>
        </div>
      )}
    </div>
  );
};

export default FloatingNotification;
